import React, { useEffect, useState } from 'react';
import { Activity, CircleAlert, CircleCheck, Info, KeyRound, Lock, Trash2, TriangleAlert } from 'lucide-react';
import { Button, Loader } from '../ui';
import { aiService, apiErrorMessage } from '../../services/api';
import { MODELS, PROVIDERS, ago, maskedKey, modelLabel, providerLabel } from '../../utils/ai';
import './AccountAI.css';

const MODES = [
  { value: 'FALLBACK', label: 'Só quando a do sistema não responder', hint: 'Recomendado: sua chave fica de reserva' },
  { value: 'ALWAYS', label: 'Sempre', hint: 'Ignora a do sistema e o limite diário' },
];

/** Minha conta › Inteligência artificial: o que a pessoa usa hoje e a chave própria de reserva. */
export function AccountAI({ data, onChange }) {
  const providers = PROVIDERS.filter(p => data.providersAllowed.includes(p.value) && p.value !== 'ollama');
  const key = data.key;
  const [provider, setProvider] = useState(key?.provider || providers[0]?.value || 'anthropic');
  const [model, setModel] = useState(key?.model || MODELS[provider]?.[0]?.id || '');
  const [apiKey, setApiKey] = useState('');
  const [mode, setMode] = useState(key?.mode || 'FALLBACK');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [latency, setLatency] = useState(null);

  useEffect(() => {
    if (!key) return;
    setProvider(key.provider);
    setModel(key.model);
    setMode(key.mode);
  }, [key]);

  const changeProvider = (p) => {
    setProvider(p);
    setModel(MODELS[p]?.[0]?.id || '');
    if (key && p !== key.provider) setApiKey('');
  };

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      await aiService.saveMyKey({ provider, model, apiKey: apiKey || undefined, mode });
      setApiKey('');
      setLatency(null);
      onChange();
    } catch (err) {
      setError(apiErrorMessage(err, 'Não deu para salvar a chave.'));
    } finally {
      setBusy(null);
    }
  };

  const test = async () => {
    setBusy('test');
    setError(null);
    try {
      const r = await aiService.testMyKey();
      setLatency(r.latencyMs);
      onChange();
    } catch (err) {
      setError(apiErrorMessage(err, 'Não deu para testar.'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    setBusy('delete');
    try {
      await aiService.deleteMyKey();
      setApiKey('');
      onChange();
    } finally {
      setBusy(null);
    }
  };

  const status = data.status || {};
  const usingOwn = status.viaUserKey;
  const used = data.messagesToday;
  const limit = data.system.messagesPerDay;
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const changed = !key || apiKey || provider !== key.provider || model !== key.model || mode !== key.mode;
  const list = MODELS[provider] || [];

  return (
    <div className="aac">
      <section className="aac-now">
        <div className="aac-now__text">
          <span className="aac-eyebrow">Você usa hoje</span>
          <strong>{usingOwn ? `Sua chave · ${modelLabel(key?.model)}` : `Chave do sistema · ${modelLabel(data.system.model).replace(/^Claude /, '')}`}</strong>
          <span>{usingOwn
            ? (key?.mode === 'ALWAYS' ? 'Você escolheu usar sempre a sua chave.' : 'A do sistema não respondeu; o tutor mostra "via sua chave".')
            : data.system.configured ? 'Configurada pelo administrador. Nada para fazer aqui.' : 'O administrador ainda não configurou a IA.'}</span>
        </div>
        {!usingOwn || key?.mode !== 'ALWAYS' ? (
          <div className="aac-now__meter">
            <span><span>Mensagens hoje</span><b>{used} de {limit}</b></span>
            <span className="aac-bar"><span style={{ width: `${pct}%` }} /></span>
            <small>Renova às 00h</small>
          </div>
        ) : null}
      </section>

      <section className="aac-card">
        <h2 className="aac-card__title">
          <KeyRound size={15} aria-hidden="true" />Minha chave de API
          <span className="aac-badge">Reserva</span>
        </h2>
        <p className="aac-text">
          Opcional. Se a IA do sistema ficar fora do ar ou chegar no limite, ela continua funcionando com a sua chave
          {providers.length ? ` da ${providers.map(p => p.label).join(' ou da ')}` : ''}. O uso aparece na sua conta do provedor.
        </p>
        <div className="aac-fields">
          <label className="aac-field">
            <span>Provedor</span>
            <select className="ss-input" value={provider} onChange={(e) => changeProvider(e.target.value)}>
              {providers.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </label>
          <label className="aac-field">
            <span>Chave</span>
            <input className="ss-input aac-mono" type="password" autoComplete="off" value={apiKey}
                   onChange={(e) => setApiKey(e.target.value)}
                   placeholder={key && key.provider === provider ? maskedKey(key.provider, key.keyLast4) : provider === 'anthropic' ? 'sk-ant-...' : 'sk-...'} />
          </label>
          <label className="aac-field">
            <span>Modelo</span>
            {list.length ? (
              <select className="ss-input" value={list.some(m => m.id === model) ? model : '__other'}
                      onChange={(e) => setModel(e.target.value === '__other' ? '' : e.target.value)}>
                {list.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                {!list.some(m => m.id === model) && <option value="__other">{model || 'Outro'}</option>}
              </select>
            ) : (
              <input className="ss-input aac-mono" value={model} onChange={(e) => setModel(e.target.value)} />
            )}
          </label>
        </div>
        <div className="aac-field">
          <span>Quando usar a minha chave</span>
          <div className="aac-modes" role="radiogroup" aria-label="Quando usar a minha chave">
            {MODES.map(m => (
              <button key={m.value} type="button" role="radio" aria-checked={mode === m.value}
                      className={`aac-mode ${mode === m.value ? 'is-active' : ''}`} onClick={() => setMode(m.value)}>
                <strong>{m.label}</strong>
                <span>{m.hint}</span>
              </button>
            ))}
          </div>
          {mode === 'FALLBACK' && data.onUnavailable !== 'USE_USER_KEY' && (
            <p className="aac-note"><Info size={14} aria-hidden="true" />Hoje o administrador pausa a IA quando a do sistema falha; a reserva só vale quando ele escolher "Usar a chave da pessoa".</p>
          )}
        </div>
        {error && <p className="aac-error" role="alert"><CircleAlert size={15} aria-hidden="true" />{error}</p>}
        <div className="aac-foot">
          <span className="aac-privacy"><Lock size={13} aria-hidden="true" />Guardada criptografada e usada só nas suas conversas. Nem o admin consegue ver.</span>
          {key && <Button variant="ghost" icon={Trash2} onClick={remove} disabled={!!busy}>Remover</Button>}
          <Button variant="outline" icon={Activity} onClick={test} disabled={!key || !!busy || changed}
                  title={changed && key ? 'Salve antes de testar' : undefined}>{busy === 'test' ? 'Testando...' : 'Testar'}</Button>
          <Button onClick={save} disabled={!!busy || !changed || (!key && !apiKey) || !model}>{busy === 'save' ? 'Salvando...' : 'Salvar chave'}</Button>
        </div>
      </section>

      {key && (
        <div className="aac-states">
          {key.lastTestOk === true && (
            <div className="aac-state aac-state--ok">
              <CircleCheck size={16} aria-hidden="true" />
              <span><strong>Chave funcionando</strong>Testada {ago(key.lastTestAt)} · {providerLabel(key.provider)} respondeu{latency != null ? ` em ${(latency / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s` : ''}.</span>
            </div>
          )}
          {key.lastTestOk === false && (
            <div className="aac-state aac-state--bad">
              <TriangleAlert size={16} aria-hidden="true" />
              <span><strong>Chave recusada</strong>{key.lastError || 'O provedor não aceitou.'} Confira se copiou inteira ou se tem saldo.</span>
            </div>
          )}
          {usingOwn && (
            <div className="aac-state aac-state--warn">
              <Info size={16} aria-hidden="true" />
              <span><strong>Usando a sua chave agora</strong>{key.mode === 'ALWAYS' ? 'Modo "Sempre": o limite diário não se aplica.' : 'A do sistema não respondeu (limite ou fora do ar); o tutor mostra "via sua chave".'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Carrega os dados da aba; null enquanto carrega, false se a pessoa não pode ter chave própria. */
export function useAccountAI(enabled) {
  const [data, setData] = useState(null);
  const load = React.useCallback(() => {
    if (!enabled) { setData(false); return; }
    aiService.myKey().then(setData).catch(() => setData(false));
  }, [enabled]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

export function AccountAILoading() {
  return <Loader />;
}
